import React, { useMemo } from 'react'

import { Redirect } from 'react-router'

import { platformUnitHasDoubleVerify } from './lib'

import CrossChannelDashboard, { CrossChannelPerformance } from 'components/CrossChannelDashboard'

import {
  useLocation,
  useNavigation,
  usePlatformUnitProviderInfo,
} from '@percept/hooks'

import {
  useReferenceDate,
  useReferenceDateBehaviour,
  useSavedQueryStrings,
} from 'hooks'

import { useActiveOrgEnvironment } from './hooks'

import { find, get } from 'lodash-es'

import { isBefore } from 'date-fns'

import { getPrefixedQueryString, isPlatformUnitContainer } from '@percept/utils'

import { deriveCanonicalReferenceDate, deriveLatestAvailableReferenceDate } from 'components/ReferenceDates'

import { providerURLMap } from '@percept/constants'

import { DoubleVerifyProvider, PlatformUnit, PlatformUnitLeaf, PrimaryPerformanceDataProvider } from '@percept/types'


export const Organisation = (): JSX.Element => {

  const {
    activePlatformUnit,
    rootPlatformUnit,
    defaultPlatformUnitId,
    providers,
    performanceProviders,
    doubleVerifyProviders,
    ...activeOrgEnvironment
  } = useActiveOrgEnvironment()

  const seriesListing = (
    activePlatformUnit && (activePlatformUnit as PlatformUnitLeaf).report_series || []
  )

  const location = useLocation()

  const org_unit_id = activePlatformUnit && activePlatformUnit.id

  const hasDoubleVerify = !!(activePlatformUnit && platformUnitHasDoubleVerify(activePlatformUnit))

  const [platformUnitProviderInfo] = usePlatformUnitProviderInfo({
    org_unit_id,
  })

  const [activeReferenceDate] = useReferenceDate()

  const [referenceDateBehaviour] = useReferenceDateBehaviour()

  const [savedQueryStrings] = useSavedQueryStrings()

  const referenceDate = useMemo(() => {
    return deriveCanonicalReferenceDate({
      providerInfo: platformUnitProviderInfo.data,
      referenceDateBehaviour,
      activeReferenceDate,
      enabledProviders: [
        ...performanceProviders,
        ...doubleVerifyProviders,
      ]
    })
  }, [
    platformUnitProviderInfo.data, referenceDateBehaviour, activeReferenceDate,
    performanceProviders, doubleVerifyProviders,
  ])

  const enabledDoubleVerifyProviders: DoubleVerifyProvider[] | undefined = useMemo(() => {
    if( !hasDoubleVerify ){
      return []
    }
    const providerInfoData = platformUnitProviderInfo.data
    if( !(providerInfoData && referenceDate) ){
      return undefined
    }
    return doubleVerifyProviders.reduce( (acc, provider) => {
      const doubleVerifyReferenceDate = get(providerInfoData[provider], 'reference_date', null)
      if( doubleVerifyReferenceDate && !isBefore(doubleVerifyReferenceDate, referenceDate) ){
        acc.push(provider)
      }
      return acc
    }, [] as DoubleVerifyProvider[])
  }, [platformUnitProviderInfo.data, referenceDate, doubleVerifyProviders, hasDoubleVerify])

  const latestAvailableReferenceDate: Date | null = useMemo(() => {
    return deriveLatestAvailableReferenceDate({
      providerInfo: platformUnitProviderInfo.data,
      enabledProviders: [
        ...performanceProviders,
        ...doubleVerifyProviders,
      ]
    })
  }, [platformUnitProviderInfo.data, performanceProviders, doubleVerifyProviders])

  const { loading } = activeOrgEnvironment

  const navigate = useNavigation()

  const singleProvider: PrimaryPerformanceDataProvider | null = (
    providers.length === 1 ?
      providers[0] :
      null
  )

  const hasPerformanceReporting = !!get(
    activePlatformUnit, ['performance_reporting', 'enabled']
  )

  // Handle root redirect
  if( location.pathname === '/' && defaultPlatformUnitId ){
    return (
      <Redirect
        to={{
          pathname: `/dashboards/${defaultPlatformUnitId}`,
          search: savedQueryStrings.dashboardOverview,
          state: {
            track: true,
          },
        }} />
    )
  }
  
  // Handle redirect for empty parent orgs / orgs with no performance reporting
  if( activePlatformUnit && !hasPerformanceReporting ){

    if( isPlatformUnitContainer(activePlatformUnit) ){

      const nextValidChild = find(activePlatformUnit.children, unit => {
        return (
          unit.performance_reporting && unit.performance_reporting.enabled
        ) || (
          !isPlatformUnitContainer(unit) && get(unit.report_series, 'length')
        )
      }) as PlatformUnit | undefined

      if( nextValidChild && nextValidChild.id ){
        return (
          <Redirect
            to={{
              pathname: `/dashboards/${nextValidChild.id}`,
              search: savedQueryStrings.dashboardOverview,
              state: {
                track: true,
              },
            }} />
        )
      }

    }else{

      if( activePlatformUnit.report_series && activePlatformUnit.report_series.length ){
        // Redirect to either insights or first available series if no perf reporting
        const redirectSeries = activePlatformUnit.report_series[0]
        return (
          <Redirect
            to={{
              pathname: `/series/${redirectSeries.id}`,
              search: savedQueryStrings.series,
              state: {
                track: true,
              },
            }} />
        )
      }else{
        console.warn('No valid route detected for this unit', activePlatformUnit)
      }
    }

  }

  // NOTE - single provider redirect disabled to show consistent overview
  //
  // if( singleProvider && activePlatformUnit && rootPlatformUnit ){
  //   const providerURLFragment = providerURLMap[singleProvider]
  //   const redirectRoute = (
  //     `/dashboards/${activePlatformUnit.id}/${providerURLFragment}`
  //   )
  //   return (
  //     <Redirect to={{
  //       pathname: redirectRoute,
  //       state: {
  //         track: true,
  //       },
  //     }} />
  //   )
  // }
  
  const isPerformanceView = location.pathname.endsWith('performance')

  const DashboardComponent = (
    isPerformanceView ?
      CrossChannelPerformance :
      CrossChannelDashboard
  )

  return (
    <DashboardComponent
      key={isPerformanceView ? 'performance' : 'structure'}
      displayLoader={loading}
      showContent={!loading}
      platformUnit={activePlatformUnit}
      providers={performanceProviders}
      doubleVerifyProviders={enabledDoubleVerifyProviders || null}
      providerInfo={platformUnitProviderInfo.data}
      referenceDate={referenceDate}
      latestAvailableReferenceDate={latestAvailableReferenceDate}
      seriesListing={seriesListing}
      onSeriesClick={
        seriesListing ?
          (({ id }): void => {
            navigate(
              `/series/${id}`,
              { search: getPrefixedQueryString(savedQueryStrings.series) }
            )
          }) :
          undefined
      } />
  )

}

export default Organisation
